import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Button,
  Divider,
  Paper,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Tab,
  Tabs,
  Autocomplete,
  CircularProgress,
  Alert,
  IconButton,
  Stack
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import TabPanel from '../common/TabPanel';

// Components
import PhotoUploader from '../common/PhotoUploader';
import MapPicker from '../common/MapPicker';
import HoursEditor from '../common/HoursEditor';
import MenuItemsEditor from '../common/MenuItemsEditor';
import SocialMediaEditor from '../common/SocialMediaEditor';

const bbqStyles = [
  'Texas', 'Memphis', 'Kansas City', 'Carolina', 'Alabama', 
  'St. Louis', 'Chicago', //'Hawaiian', 'Korean', 'Japanese'
];

const smokerTypes = [
  'Offset Wood Smoker', 'Pellet Smoker', 'Vertical Smoker',
  'Barrel Smoker', 'Kamado Grill', 'Pit Smoker', 'Electric Smoker',
  'Reverse Flow Smoker', 'Cabinet Smoker', 'Custom'
];

function RestaurantForm({ initialData, onSubmit, isEditing = false }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    street: '',
    city: '',
    state: '',
    phoneNumber: '',
    website: '',
    description: '',
    bbqStyle: '',
    established: '',
    pitmaster: '',
    smokerType: '',
    specialities: '',
    openForBusiness: true,
    isFeatured: false,
    dineIn: false,
    takeout: false,
    delivery: false,
    outside: false,
    default_address: true,
    logoPhotoURL: '',
    photoUrls: [],
    latitude: null,
    longitude: null,
    rating: 0,
    averageWaitTimes: {
      "Weekday Lunch": 0,
      "Weekday Dinner": 0,
      "Weekend": 0
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // If editing, populate form with initial data
  useEffect(() => {
    if (initialData) {
      const data = { ...initialData };
      
      // Extract latitude and longitude from latlng GeoPoint
      if (data.latlng) {
        data.latitude = data.latlng.latitude || data.latlng._latitude;
        data.longitude = data.latlng.longitude || data.latlng._longitude;
      }
      
      // Ensure averageWaitTimes exists
      data.averageWaitTimes = data.averageWaitTimes || {
        "Weekday Lunch": 0,
        "Weekday Dinner": 0,
        "Weekend": 0
      };
      
      setFormData(data);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleWaitTimeChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      averageWaitTimes: {
        ...prev.averageWaitTimes,
        [name]: parseInt(value, 10) || 0
      }
    }));
  };

  const handleLocationChange = (lat, lng) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));
  };

  const handleRatingChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      rating: newValue
    }));
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await onSubmit(formData);
      setSuccess(true);
      
      // If creating a new restaurant, reset form
      if (!isEditing) {
        setFormData({
          name: '',
          street: '',
          city: '',
          state: '',
          phoneNumber: '',
          website: '',
          description: '',
          bbqStyle: '',
          established: '',
          pitmaster: '',
          smokerType: '',
          specialities: '',
          openForBusiness: true,
          isFeatured: false,
          dineIn: false,
          takeout: false,
          delivery: false,
          outside: false,
          default_address: true,
          logoPhotoURL: '',
          photoUrls: [],
          latitude: null,
          longitude: null,
          rating: 0,
          averageWaitTimes: {
            "Weekday Lunch": 0,
            "Weekday Dinner": 0,
            "Weekend": 0
          }
        });
      }

      // Navigate back to list after short delay
      setTimeout(() => {
        navigate('/restaurants');
      }, 1500);
    } catch (error) {
      console.error('Error submitting restaurant data:', error);
      setError(error.message || 'Failed to save restaurant data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/restaurants')}
        >
          Back to list
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Restaurant'}
        </Button>
      </Box>

      {/* Status messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Restaurant saved successfully!
        </Alert>
      )}

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Basic Info" />
          <Tab label="Location" />
          <Tab label="Details" />
          <Tab label="Photos" />
          <Tab label="Hours" />
          <Tab label="Menu Items" />
          <Tab label="Social Media" />
        </Tabs>
      </Paper>

      {/* Basic Info Tab */}
      <TabPanel value={tabValue} index={0}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Restaurant Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={bbqStyles}
                value={formData.bbqStyle}
                onChange={(event, newValue) => {
                  setFormData(prev => ({
                    ...prev,
                    bbqStyle: newValue
                  }));
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="BBQ Style" 
                    name="bbqStyle"
                    onChange={handleChange}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Year Established"
                name="established"
                value={formData.established}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Restaurant Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Restaurant Rating
                </Typography>
                <Rating
                  name="rating"
                  value={formData.rating}
                  precision={0.5}
                  onChange={handleRatingChange}
                  size="large"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.openForBusiness}
                    onChange={handleChange}
                    name="openForBusiness"
                    color="primary"
                  />
                }
                label="Open for Business"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isFeatured}
                    onChange={handleChange}
                    name="isFeatured"
                    color="primary"
                  />
                }
                label="Featured Restaurant"
              />
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>

      {/* Social Media Tab */}
      <TabPanel value={tabValue} index={6}> {/* Make sure the index matches your tabs order */}
  <Paper sx={{ p: 3, mb: 3 }}>
    <Typography variant="h6" gutterBottom>
      Social Media Accounts
    </Typography>
    <Divider sx={{ mb: 3 }} />

    <SocialMediaEditor
      socialMediaLinks={formData.socialMedia || {}}
      onChange={(socialMedia) => {
        setFormData(prev => ({
          ...prev,
          socialMedia
        }));
      }}
    />
  </Paper>
</TabPanel>


      {/* Location Tab */}
      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Location Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Street Address"
                name="street"
                value={formData.street}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="State"
                name="state"
                value={formData.state}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Zip Code"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Map Location
              </Typography>
              <MapPicker 
                latitude={formData.latitude} 
                longitude={formData.longitude} 
                onChange={handleLocationChange} 
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Latitude"
                name="latitude"
                value={formData.latitude || ''}
                onChange={handleChange}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Longitude"
                name="longitude"
                value={formData.longitude || ''}
                onChange={handleChange}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>

      {/* Details Tab */}
      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Restaurant Details
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Pitmaster"
                name="pitmaster"
                value={formData.pitmaster}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={smokerTypes}
                value={formData.smokerType}
                onChange={(event, newValue) => {
                  setFormData(prev => ({
                    ...prev,
                    smokerType: newValue
                  }));
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Smoker Type" 
                    name="smokerType"
                    onChange={handleChange}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Specialities"
                name="specialities"
                value={formData.specialities}
                onChange={handleChange}
                placeholder="E.g., Brisket, Ribs, Pulled Pork"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Service Options
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.dineIn}
                      onChange={handleChange}
                      name="dineIn"
                      color="primary"
                    />
                  }
                  label="Dine In"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.takeout}
                      onChange={handleChange}
                      name="takeout"
                      color="primary"
                    />
                  }
                  label="Takeout"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.delivery}
                      onChange={handleChange}
                      name="delivery"
                      color="primary"
                    />
                  }
                  label="Delivery"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.outside}
                      onChange={handleChange}
                      name="outside"
                      color="primary"
                    />
                  }
                  label="Outside Seating"
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Average Wait Times (minutes)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Weekday Lunch"
                    name="Weekday Lunch"
                    value={formData.averageWaitTimes["Weekday Lunch"]}
                    onChange={handleWaitTimeChange}
                    InputProps={{ 
                      endAdornment: <InputAdornment position="end">min</InputAdornment> 
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Weekday Dinner"
                    name="Weekday Dinner"
                    value={formData.averageWaitTimes["Weekday Dinner"]}
                    onChange={handleWaitTimeChange}
                    InputProps={{ 
                      endAdornment: <InputAdornment position="end">min</InputAdornment> 
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Weekend"
                    name="Weekend"
                    value={formData.averageWaitTimes["Weekend"]}
                    onChange={handleWaitTimeChange}
                    InputProps={{ 
                      endAdornment: <InputAdornment position="end">min</InputAdornment> 
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>

      {/* Photos Tab */}
      <TabPanel value={tabValue} index={3}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Restaurant Photos
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" gutterBottom>
              Logo
            </Typography>
            <PhotoUploader
              currentPhoto={formData.logoPhotoURL}
              onPhotoChange={(url) => {
                setFormData(prev => ({
                  ...prev,
                  logoPhotoURL: url
                }));
              }}
              restaurantId={formData.id}
              isLogo={true}
            />
          </Box>

          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Restaurant Photos
            </Typography>
            <PhotoUploader
              photoList={formData.photoUrls || []}
              onPhotosChange={(urls) => {
                setFormData(prev => ({
                  ...prev,
                  photoUrls: urls
                }));
              }}
              restaurantId={formData.id}
              isLogo={false}
              multiple={true}
            />
          </Box>
        </Paper>
      </TabPanel>

      {/* Hours Tab */}
      <TabPanel value={tabValue} index={4}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Business Hours
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <HoursEditor
            restaurantId={formData.id}
            onChange={(hoursData) => {
              console.log('Hours updated:', hoursData);
            }}
          />
        </Paper>
      </TabPanel>

      {/* Menu Items Tab */}
      <TabPanel value={tabValue} index={5}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Menu Items
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <MenuItemsEditor
            restaurantId={formData.id}
            onChange={(menuItems) => {
              console.log('Menu items updated:', menuItems);
            }}
          />
        </Paper>
      </TabPanel>

      {/* Footer Actions */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/restaurants')}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          disabled={loading}
        >
          {loading ? (
            <>
              <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />
              Saving...
            </>
          ) : (
            isEditing ? 'Update Restaurant' : 'Create Restaurant'
          )}
        </Button>
      </Box>
    </Box>
  );
}

export default RestaurantForm;